// src/auth/components/HasPermission.jsx
import React from 'react';
import { hasPermission, hasAnyPermission, hasAllPermissions } from './authUtils';

/**
 * HasPermission Component
 * 
 * Wrapper component that conditionally renders children based on user permissions.
 * Supports single permission, any of multiple, or all of multiple permissions.
 * Can also render fallback content when permission is denied.
 * 
 * @param {string} permission - Single permission code
 * @param {string[]} anyPermissions - Array of permissions (user needs ANY)
 * @param {string[]} allPermissions - Array of permissions (user needs ALL)
 * @param {React.ReactNode} children - Content to render if permission is granted
 * @param {React.ReactNode} fallback - Content to render if permission is denied (optional)
 * @param {boolean} hide - If true, renders nothing when denied (default: true)
 * 
 * @example
 * // Single permission - hide if no access
 * <HasPermission permission={PERMISSIONS.USER_CREATE}>
 *   <button>Add User</button>
 * </HasPermission>
 * 
 * @example
 * // Any of multiple permissions
 * <HasPermission anyPermissions={[PERMISSIONS.USER_CREATE, PERMISSIONS.USER_UPDATE]}>
 *   <button>Edit</button>
 * </HasPermission>
 * 
 * @example
 * // All of multiple permissions with fallback
 * <HasPermission 
 *   allPermissions={[PERMISSIONS.BUDGET_UPDATE, PERMISSIONS.BUDGET_APPROVE]}
 *   fallback={<span className="text-gray-400">No access</span>}
 * >
 *   <button>Approve Budget</button>
 * </HasPermission>
 * 
 * @example
 * // Complex UI section
 * <HasPermission permission={PERMISSIONS.PROJECT_VIEW}>
 *   <div className="project-details">
 *     <h2>Project Details</h2>
 *     <ProjectInfo />
 *     <ProjectTimeline />
 *   </div>
 * </HasPermission>
 */
const HasPermission = ({
  permission,
  anyPermissions,
  allPermissions,
  children,
  fallback = null,
  hide = true,
}) => {
  // Check permissions
  let hasAccess = false;

  if (permission) {
    // Single permission check
    hasAccess = hasPermission(permission);
  } else if (anyPermissions && anyPermissions.length > 0) {
    // Any of multiple permissions
    hasAccess = hasAnyPermission(anyPermissions);
  } else if (allPermissions && allPermissions.length > 0) {
    // All of multiple permissions
    hasAccess = hasAllPermissions(allPermissions);
  } else {
    // No permission specified - grant access by default
    // This allows the component to be used as a simple wrapper
    hasAccess = true;
  }

  // Render based on access
  if (hasAccess) {
    return <>{children}</>;
  }

  // No access - render fallback or nothing
  if (hide) {
    return null;
  }

  return <>{fallback}</>;
};

/**
 * HasPermissionOr Component
 * 
 * Convenience wrapper for checking ANY of multiple permissions.
 * Alias for HasPermission with anyPermissions prop.
 * 
 * @example
 * <HasPermissionOr permissions={[PERMISSIONS.USER_CREATE, PERMISSIONS.USER_UPDATE]}>
 *   <button>Edit</button>
 * </HasPermissionOr>
 */
export const HasPermissionOr = ({ permissions, children, fallback, hide = true }) => {
  return (
    <HasPermission anyPermissions={permissions} fallback={fallback} hide={hide}>
      {children}
    </HasPermission>
  );
};

/**
 * HasPermissionAnd Component
 * 
 * Convenience wrapper for checking ALL of multiple permissions.
 * Alias for HasPermission with allPermissions prop.
 * 
 * @example
 * <HasPermissionAnd permissions={[PERMISSIONS.BUDGET_UPDATE, PERMISSIONS.BUDGET_APPROVE]}>
 *   <button>Approve</button>
 * </HasPermissionAnd>
 */
export const HasPermissionAnd = ({ permissions, children, fallback, hide = true }) => {
  return (
    <HasPermission allPermissions={permissions} fallback={fallback} hide={hide}>
      {children}
    </HasPermission>
  );
};

/**
 * WithPermission HOC (Higher Order Component)
 * 
 * HOC wrapper for permission checking.
 * Useful for wrapping entire components with permission checks.
 * 
 * @param {React.Component} Component - Component to wrap
 * @param {string} permission - Required permission
 * @param {React.Component} FallbackComponent - Component to render when denied (optional)
 * 
 * @example
 * const ProtectedUserForm = WithPermission(UserForm, PERMISSIONS.USER_CREATE);
 * 
 * // Usage
 * <ProtectedUserForm />
 * 
 * @example
 * // With fallback
 * const NoAccessMessage = () => <div>You don't have access</div>;
 * const ProtectedPage = WithPermission(AdminPage, PERMISSIONS.SYSTEM_SETTINGS, NoAccessMessage);
 */
export const WithPermission = (Component, permission, FallbackComponent = null) => {
  return (props) => {
    const hasAccess = hasPermission(permission);

    if (hasAccess) {
      return <Component {...props} />;
    }

    if (FallbackComponent) {
      return <FallbackComponent />;
    }

    return null;
  };
};

export default HasPermission;